import type {ISOString, Nullable} from './general.js';
import type {UUID} from './strings.js';
import type {Tag} from './audiences/tag.js';
import type {User} from './user.js';

export type JourneyCustomAttribute = {
  id: UUID;
  label: Nullable<string>;
  value: Nullable<string>;
  type: JourneyCustomAttributeType;
  open: boolean;
};

export enum JourneyCustomAttributeType {
  JOIN_FORMULA = 'join-formula',
  ATTRIBUTE_FORMULA = 'attribute-formula',
  TEXT = 'text',
  COMPUTED_FIELD = 'computed-field',
}

export const JourneyCustomAttributeTypeLabels = {
  [JourneyCustomAttributeType.JOIN_FORMULA]: 'Join Formula',
  [JourneyCustomAttributeType.ATTRIBUTE_FORMULA]: 'Attribute Formula',
  [JourneyCustomAttributeType.TEXT]: 'Text',
  [JourneyCustomAttributeType.COMPUTED_FIELD]: 'Computed Field',
} as const satisfies Record<JourneyCustomAttributeType, string>;

export type CustomAttributesMetadata = {
  customAttributes: Nullable<JourneyCustomAttribute[]>;
};

export type NodeMetric = {
  active: Nullable<number>;
  completed: Nullable<number>;
  exited: Nullable<number>;
};

export type NodeMetricState =
  | 'unpublished'
  | 'processing'
  | 'unavailable'
  | 'available';

export type NodeProcessorMetadata = {
  processing: boolean;
  metrics: Nullable<NodeMetric>;
};

export const sidebarPanels = [
  {
    name: 'insights',
    button: 'insights',
    icon: 'bar_chart',
  },
  {
    name: 'errors',
    button: 'errors',
    icon: 'warning',
  },
  {
    name: 'history',
    button: 'history',
    icon: 'calendar_month',
  },
  {
    name: 'health',
    button: 'health',
    svg: true,
    icon: '/assets/notes_medical_icon.svg',
  },
] as const;

export type SidebarPanel = (typeof sidebarPanels)[number]['name'];

export enum SidebarPanelName {
  INSIGHTS = 'insights',
  ERRORS = 'errors',
  HISTORY = 'history',
  HEALTH = 'health',
}

export enum JourneyNodeType {
  ENTRY = 'entry',
  EXIT = 'exit',
  DELAY = 'delay',
  CRITERIA = 'criteria',
  CRITERIA_GROUP = 'criteria-group',
  DESTINATION = 'destination',
  CRM_ACTION = 'crm',
  EXPERIMENT = 'experiment',
  VARIATION = 'variation',
}

export type ManualModeRequestInfo = {
  journeyId: Nullable<number> | undefined;
  datasetGroupId: number;
  nodeId: string;
  direction: ManualModeDirection;
};

export const ManualModeDirection = {
  UPSTREAM: 'upstream',
  SELF: 'self',
  DOWNSTREAM: 'downstream',
} as const;

/**
 * Based on RRule
 * {@link https://jakubroztocil.github.io/rrule/}
 */
export type JourneyScheduleData = {
  schedule_type: Nullable<string>;
  frequency: Nullable<string>;
  occurrence?: Array<{type: string; value: number[]}>;
  start_type: Nullable<string>;
  start_time?: Nullable<{
    time?: ISOString;
    timezone: string;
    timezoneOffset: string;
  }>;
  end_type: Nullable<string>;
  end_time?: Nullable<{
    time?: ISOString;
    timezone: string;
    timezoneOffset: string;
  }>;
  /* Yet unsupported */
  // count?: number;
  // interval?: number;
};

export type ManualModeDirection =
  (typeof ManualModeDirection)[keyof typeof ManualModeDirection];

export type MatchFieldValue = string;
export type SelectedFieldValue = Nullable<string>;
export type MatchField = {
  label: string;
  value: string;
  compatibleTypes?: string[];
  required?: boolean;
  match_type?: MatchType;
  caption?: string;
  sourceType?: MatchFieldSourceType;
};

export enum MatchFieldSourceType {
  DATASET = 'dataset',
  REALTIME = 'realtime',
}

export type MappedMatchField = {
  label: string;
  mappedValue: Record<MatchFieldValue, Nullable<SelectedFieldValue> | string[]>;
  required: boolean;
  match_type?: MatchType;
  tag?: MatchFieldTag;
  sourceType?: MatchFieldSourceType;
};

export enum MatchFieldTag {
  DEFAULT = 'default',
  EMAIL_TEMPLATE = 'campaign-triggers--email-template',
}
export type MatchType = LinkedinMatchType | GoogleMatchType;

export enum LinkedinMatchType {
  CONTACT = 'Contact',
  COMPANY = 'Company',
}

export enum GoogleMatchType {
  CONTACT = 'Contact',
  DEVICE = 'Device',
}

export const ValidationStates = {
  INITIAL: 'initial',
  VALID: 'valid',
  INVALID: 'invalid',
} as const;

export type ValidationState =
  (typeof ValidationStates)[keyof typeof ValidationStates];

// TODO: This was added during journey builder hackathon, we should move or remove it
export type Journey = {
  id: number;
  name: string;
  datasetGroupId: number;
  audienceId: number;
  status: string;
  settings: object;
  tagList: Tag[];
  creatorMetadata: User;
  audienceMetadata: Record<string, string>;
  datasetGroupMetadata: Record<string, string>;
  statistics: Record<string, string>;
  updatedAt: Date;
  createdAt: string;
};

export type DropdownItem = {
  label: string;
  disabled: boolean;
  onClick: () => void;
  danger: boolean;
  tooltip?: string;
};

export const JOURNEY_ACTION_OPTIONS = {
  SAVE: 'save',
  PUBLISH: 'publish',
  DELETE: 'delete',
  HARD_STOP: 'hard-stop',
  HARD_RESET: 'hard-reset',
  COPY: 'copy',
  DRAIN: 'drain',
} as const;

export type JourneyActionOption =
  (typeof JOURNEY_ACTION_OPTIONS)[keyof typeof JOURNEY_ACTION_OPTIONS];

export const ATTRIBUTE_TYPE = {
  TEXT: 'text',
  ATTRIBUTE_FORMULA: 'attribute-formula',
} as const;

export type AttributeType =
  (typeof ATTRIBUTE_TYPE)[keyof typeof ATTRIBUTE_TYPE];

export interface CustomAttribute {
  id: string;
  name: string;
  type: AttributeType;
  value: string;
  isEditorExpanded?: boolean;
  open: boolean;
  hasUpstreamCollision?: boolean;
}
